import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../ts/Glob';
import { ProgressBar } from '../../../../_common/platform/tsx/ProgressBar';
import NavTabsScrollable from '../../../../_common/platform/tsx/NavTabsScrollable';
import serializeWorkspace from '../../../../_common/platform/ts/serializeWorkspace';
import * as permissions from '../../../../_common/platform/ts/permissions';
import * as Utilities from '../../../../_common/platform/ts/Utilities';
import { extend as extendTheme, IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import { IWidget } from '../../ts/Interfaces';
import { ITab } from '../../ts/stores/AppStore';
import { useElementsReorderingHook } from '../../../../_common/platform/ts/DragDropCommon';
import { themeVars } from '../../ts/themes/themeConstants';

const MIN_MENU_WIDTH = 184;
const MIN_TEXT_WIDTH = 60;

interface IMenuPosition {
    tabID: number;
    top: number;
    left: number;
    index: number;
}

function ContextMenuItem(props: { title: string; onClick(): void }) {
    const handleOnClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (props.onClick != null) {
            props.onClick();
        }
    };

    return (
        <div className="dropdown-item" title={props.title} onClick={handleOnClick}>
            <a href="#">{props.title}</a>
        </div>
    );
}

function ContextMenu(props: { menuPosition: IMenuPosition; glob: IGlob; onClose(): void }) {
    if (props.menuPosition == null) {
        return null;
    }

    const getOnCloseAllOpenedTabsHandler = (exceptTabIndex?: number, closeToTheRight?: boolean) => {
        return () => {
            props.glob.AppActions.closeAllOpenedTabs(exceptTabIndex, closeToTheRight);
            props.onClose();
        };
    };

    const style: React.CSSProperties = {
        position: 'fixed',
        display: 'block'
    };

    if (props.menuPosition.top !== 0) {
        style.top = props.menuPosition.top + 'px';
    }

    if (props.menuPosition.left !== 0) {
        style.left = props.menuPosition.left + 'px';
    }

    return (
        <div className="tab-context-menu dropdown-menu dropup" style={style}>
            <ContextMenuItem title={'Close all tabs'} onClick={getOnCloseAllOpenedTabsHandler()} />
            <ContextMenuItem
                title={'Close other tabs'}
                onClick={getOnCloseAllOpenedTabsHandler(props.menuPosition.index)}
            />
            <ContextMenuItem
                title={'Close tabs to the right'}
                onClick={getOnCloseAllOpenedTabsHandler(props.menuPosition.index, true)}
            />
        </div>
    );
}

function WorkspaceTab(props: {
    id: number;
    index: number;
    tab: ITab;
    title?: string; // optional title
    widget: number;
    tabDisplay: number;
    showSideBar: boolean;
    allowRemoving: boolean;
    theme: IValuesCollection;
    workspaceTheme: IValuesCollection;
    isNew: boolean;
    isSame: boolean;
    glob: IGlob;

    onClick(id: number, i: number, widget: number, isNew: boolean, title: string): void;
    onRemove(id: number, i: number, isNew: boolean, isSame: boolean): void;
    onContextMenuCall(menuPosition: IMenuPosition): void;
}) {
    const [, setUpdateKey] = React.useState({});
    const navigationItem = React.useRef<HTMLLIElement>(null);

    React.useEffect(() => {
        const handleShowContextMenu = (event) => {
            event.preventDefault();

            props.onContextMenuCall({
                tabID: props.id,
                left: event.pageX,
                top: event.pageY,
                index: props.index
            });
        };

        navigationItem.current.addEventListener('contextmenu', handleShowContextMenu);

        return () => {
            if (navigationItem.current != null) {
                navigationItem.current.removeEventListener('contextmenu', handleShowContextMenu);
            }
        };
    }, [props.id]);

    React.useEffect(() => {
        const handleOnStoreChange = () => {
            setUpdateKey({});
        };

        props.glob.WorkspaceStore.addChangeListener(handleOnStoreChange);
        props.glob.AppStore.addOnTabContentStateChangedListener(handleOnStoreChange);

        return () => {
            props.glob.WorkspaceStore.removeChangeListener(handleOnStoreChange);
            props.glob.AppStore.removeOnTabContentStateChangedListener(handleOnStoreChange);
        };
    }, []);

    const handleWorkspaceTabClick = () => {
        if (typeof props.onClick === 'function') {
            props.onClick(props.id, props.index, props.widget, props.isNew, props.title);
        }
    };

    const handleWorkspaceTabRemoveClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (typeof props.onRemove === 'function') {
            props.onRemove(props.id, props.index, props.isNew, props.isSame);
        }
    };

    const getWsPathString = (arrayPath: string[], title: string, wsId: number) => {
        if (arrayPath != null && arrayPath.length > 0) {
            return '/ ' + arrayPath.join(' / ') + ' / ' + title;
        }

        // for Browse from Entity:
        if (wsId < 0) {
            return title + ': browse data';
        }

        // unknown case:
        return title;
    };

    const instance = props.glob.WorkspaceStore.getInstanceState(props.id);

    let widget: IWidget;

    if (instance != null && props.widget != null) {
        const data = instance.data;

        if (data != null && data.widgets != null) {
            const searchRes = props.glob.WorkspaceStore.getWidgetsByIdDeep(data.widgets, props.widget);

            if (searchRes != null) {
                const [foundWidget, chain, ids, types] = searchRes;

                widget = foundWidget;
            }
        }
    }

    let title: string;
    let tabName: string;

    if (props.id != null && widget != null) {
        const widgetValues = widget.values[widget.type] as any;

        title = tabName = widgetValues.name;
        title = getWsPathString(widgetValues.wsPathDir, title, props.id);
    } else if (props.id != null) {
        tabName = instance.name;
        title = getWsPathString(instance.wsPathDir, tabName, props.id);
    }

    const tabCaption = props.title || tabName;

    let removeTabElement: React.JSX.Element;

    if (props.allowRemoving) {
        const removeIconStyle: React.CSSProperties = {
            padding: '1px 0.5rem 1px 0.5rem' //EVG: I've increased size of Remove Button to make pointing at it easier.
        };

        let workspaceIsChanged = props.tab.contentChanged != null;

        if (!workspaceIsChanged) {
            workspaceIsChanged =
                instance != null &&
                !instance.loading &&
                instance.edititngStarted &&
                serializeWorkspace(instance.data, true) !== instance.originalData;
        }

        if (workspaceIsChanged) {
            removeIconStyle.color = props.theme['danger-bg'] || '#FF0000';
        }

        let classIconOnRemove: string;

        if (props.id != null && props.id < 0) {
            classIconOnRemove = 'glyphicon glyphicon-remove-circle';
        } else {
            classIconOnRemove = 'glyphicon glyphicon-remove';
        }

        removeTabElement = (
            <span className={classIconOnRemove} style={removeIconStyle} onClick={handleWorkspaceTabRemoveClick}></span>
        );
    }

    let nameElement: React.JSX.Element;

    if (instance != null && instance.loading && (tabCaption == null || tabCaption === '')) {
        nameElement = (
            <ProgressBar
                style={{
                    marginBottom: '0px',
                    minWidth: MIN_TEXT_WIDTH + 'px',
                    paddingRight: removeTabElement == null ? '0.5rem' : null
                }}
            />
        );
    } else if (tabName == null) {
        nameElement = (
            <em
                style={{
                    overflow: 'hidden',
                    display: 'block',
                    minWidth: MIN_TEXT_WIDTH + 'px',
                    paddingLeft: '0.25rem',
                    paddingRight: removeTabElement == null ? '0.75rem' : null
                }}
            >
                {'Empty'}
            </em>
        );
    } else {
        nameElement = (
            <span
                style={{
                    //textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: 'block',
                    textAlign: 'center',
                    minWidth: MIN_TEXT_WIDTH + 'px',
                    paddingLeft: '0.25rem',
                    paddingRight: removeTabElement == null ? '0.75rem' : null
                }}
            >
                {tabCaption}
            </span>
        );
    }

    let iconElement: React.JSX.Element;

    if (props.workspaceTheme != null) {
        const icon = props.workspaceTheme[themeVars.tabIcon];

        if (icon != null) {
            iconElement = <img src={icon} style={{ height: '16px', width: '16px' }} />;
        }
    }

    const style: React.CSSProperties = {
        //textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    };

    // add border for non-active TABS
    // if (props.theme['navbar-material-style']!=true){
    //     style.borderBottom = '1px solid ' + props.theme['navbar-active-link-hover-border-color']
    // }

    if (props.index === props.tabDisplay && props.theme['navbar-material-style'] !== true) {
        if (props.theme['workspace-bg'] != null) {
            style.backgroundColor = props.theme['workspace-bg'];
            style.color = Utilities.getContrastColor(style.backgroundColor);
        } else if (props.theme['body-bg'] != null) {
            style.backgroundColor = props.theme['body-bg'];
            style.color = Utilities.getContrastColor(style.backgroundColor);
        } else {
            style.backgroundColor = '#FFFFFF';
            style.color = '#000000';
        }

        style.borderBottom = 'none';
        style.height = '2rem';
    }

    return (
        <li className={'nav-item'} ref={navigationItem} title={title} onClick={handleWorkspaceTabClick}>
            <div
                className={
                    'nav-link d-flex flex-row justify-content-start ' +
                    (props.index === props.tabDisplay ? 'active ' : '')
                }
                style={style}
            >
                <div className="nav-link-inner-elements d-flex">
                    {iconElement}
                    {nameElement}
                    {removeTabElement}
                </div>
            </div>
        </li>
    );
}

function getWorkspaceNames(tabs: ITab[], glob: IGlob) {
    const workspaceNames = {};

    _.each(tabs, (tab) => {
        const workspaceID = tab.workspaceID || tab.cid;

        if (workspaceID != null) {
            const instance = glob.WorkspaceStore.getInstanceState(workspaceID, true);

            workspaceNames[workspaceID] = instance.name;
        }
    });

    return JSON.stringify(workspaceNames);
}

export default function WorkspaceTabMenu(props: {
    tabs: ITab[];
    tabDisplay: number;
    viewportWidth: number;
    theme: IValuesCollection;
    glob: IGlob;
    //editMode: boolean;

    //EVG 30.11.2021: These properties are used only to define if WorkspaceTabMenu is to be refreshed.
    navAndLogoWidth: number;
    showActions: boolean;
    workspaceIsNew: boolean;
    //isNotSaved: boolean;

    onWorkspaceRemove(id: number, tab: number, isNew: boolean, isSame: boolean): void;
}) {
    const [maxWidth, setMaxWidth] = React.useState(MIN_MENU_WIDTH);
    const [workspaceNames, setWorkspaceNames] = React.useState(getWorkspaceNames(props.tabs, props.glob));
    const [menuPosition, setMenuPosition] = React.useState<IMenuPosition>(null);
    const tabsRef = React.useRef<HTMLDivElement>(null);
    useElementsReorderingHook(
        tabsRef,
        {
            axis: 'x',
            sortableItems: '.nav-item'
        },
        (start, stop) => {
            props.glob.AppActions.reorderTabs(start, stop);
        }
    );

    React.useEffect(() => {
        const handleOnContextMenuHide = (e) => {
            setMenuPosition(null);
        };

        document.body.addEventListener('click', handleOnContextMenuHide);

        return () => {
            document.body.removeEventListener('click', handleOnContextMenuHide);
        };
    }, []);

    React.useEffect(() => {
        const handleOnStoreChange = () => {
            setWorkspaceNames(getWorkspaceNames(props.tabs, props.glob));
        };

        const handleOnDirsStoreChange = () => {
            setWorkspaceNames(getWorkspaceNames(props.tabs, props.glob));
        };

        props.glob.WorkspaceStore.addChangeListener(handleOnStoreChange);
        props.glob.WorkspacesDirsStore.addChangeListener(handleOnDirsStoreChange);

        return () => {
            props.glob.WorkspaceStore.removeChangeListener(handleOnStoreChange);
            props.glob.WorkspacesDirsStore.removeChangeListener(handleOnDirsStoreChange);
        };
    });

    const handleWorkspaceTabClick = (id: number, tab: number, widget: number, isNew: boolean, title: string) => {
        if (props.tabDisplay === tab) {
            return;
        }

        let route = '/tabs/' + tab;

        if (id != null && !isNew) {
            route += '/workspaces/' + id;
        }

        if (id != null && title != null) {
            route += '/title/' + title;
        }

        if (id != null && widget != null) {
            route += '/widgets/' + widget;
        }

        props.glob.mainRouter.navigate(route, { trigger: true });
    };

    const handleWorkspaceTabRemoveClick = (id: number, tab: number, isNew: boolean, isSame: boolean) => {
        if (props.onWorkspaceRemove != null) {
            props.onWorkspaceRemove(id, tab, isNew, isSame);
        }
    };

    const showSideBar = permissions.showSideBar();

    const workspaces: {
        id: number;
        tab: ITab;
        widget: number;
        isNew: boolean;
        isSame: boolean;
        title: string;
        allowRemoving: boolean;
    }[] = [];

    let tabs = props.tabs;

    if (!showSideBar) {
        tabs = _.filter(tabs, (tab) => {
            return tab.workspaceID != null || tab.widget != null;
        });
    }

    _.each(tabs, (tab) => {
        const id = tab.workspaceID;
        const workspaceID = id || tab.cid;

        let isSame = false;

        for (let i = 0; i < workspaces.length; i++) {
            const workspace = workspaces[i];

            if (workspace.id === workspaceID) {
                isSame = workspace.isSame = true;
                break;
            }
        }

        // VR: WorkItem [76626]: A user without Show Menu should NOT be allowed to Open new Tabs, and not to Close Open WS
        // OE: WorkItem [90461]: If tab show maximized widget it is always allowed to close it
        // OE: WorkItem [117868]: Customer should have possibility to close Tab it is not included in default workspace list (i.e. Opened via link)
        const allowRemoving = showSideBar || tab.widget != null || !tab.isDefault;

        workspaces.push({
            id: workspaceID,
            tab: tab,
            widget: tab.widget,
            isNew: id == null,
            isSame: isSame,
            title: tab.title,
            allowRemoving: allowRemoving
        });
    });

    const workspaceTabs = _.map(workspaces, (workspace, index) => {
        const id = workspace.id;

        let workspaceTheme: IValuesCollection;

        if (index === props.tabDisplay) {
            workspaceTheme = props.theme;
        } else {
            const workspaceInstance = props.glob.WorkspaceStore.getInstanceState(id);

            if (workspaceInstance != null && workspaceInstance.data != null) {
                const dataTheme = workspaceInstance.data.theme;

                if (dataTheme != null) {
                    // It used for Workspace Options only now that is why '_custom' is enougth. Add more when needed.
                    workspaceTheme = dataTheme._custom;
                    //workspaceTheme = extendTheme({}, dataTheme, dataTheme._custom);
                }
            }
        }

        return (
            <WorkspaceTab
                key={id + '_' + index}
                id={id}
                index={index}
                tab={workspace.tab}
                title={workspace.title}
                widget={workspace.widget}
                tabDisplay={props.tabDisplay}
                showSideBar={showSideBar}
                theme={props.theme}
                workspaceTheme={workspaceTheme}
                isNew={workspace.isNew}
                isSame={workspace.isSame}
                allowRemoving={workspace.allowRemoving}
                glob={props.glob}
                onClick={handleWorkspaceTabClick}
                onRemove={handleWorkspaceTabRemoveClick}
                onContextMenuCall={(position) => {
                    setMenuPosition(position);
                }}
            />
        );
    });

    return (
        <div
            className="w-100 workspace-tabs-scrollable mr-auto"
            style={{
                minWidth: MIN_MENU_WIDTH,
                maxWidth: maxWidth
            }}
            ref={tabsRef}
        >
            <NavTabsScrollable
                content={workspaceTabs}
                workspaceNames={workspaceNames}
                onMount={(newMaxWidth) => {
                    if (newMaxWidth < MIN_MENU_WIDTH) {
                        newMaxWidth = MIN_MENU_WIDTH;
                    }

                    setMaxWidth(newMaxWidth + 2);
                }}
            />

            <ContextMenu
                menuPosition={menuPosition}
                glob={props.glob}
                onClose={() => {
                    setMenuPosition(null);
                }}
            />
        </div>
    );
}
